import { LocalizedText } from "@/i18n/LocalizedText";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BadgeCheck,
  HandHelping,
  BriefcaseBusiness,
  SquareTerminal,
  MapPin,
  Bot,
  CircleDashed,
  Download,
  Info,
  Users,
  BadgePlus,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useEffect, useRef, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  authAPI,
  billingAPI,
  configAPI,
  paymentAPI,
  type Plan,
} from "@/services/api";
import { Spinner } from "@/components/ui/spinner";
import { capturePosthogEvent } from "@/lib/posthog";

const features = [
  {
    icon: <SquareTerminal size={20} />,
    name: "Prompts",
    info: "Total number of prompts you can track across all brands.",
  },
  {
    icon: <BriefcaseBusiness size={20} />,
    name: "Brands",
    info: "How many brands or projects you can monitor.",
  },
  {
    icon: <MapPin size={20}></MapPin>,
    name: "Country",
    info: "Number of countries you can run prompts from.",
  },
  {
    icon: <Bot size={20}></Bot>,
    name: "AI Platforms",
    info: "AI platforms used to run your prompts.",
  },
  {
    icon: <CircleDashed size={20}></CircleDashed>,
    name: "Runs / month",
    info: "Total prompt executions per month across all platforms.",
  },
  // {
  //   icon: <Zap size={20}></Zap>,
  //   name: "Get Cited (AI Outreach)",
  //   info: "toolltip explanation",
  // },

  {
    icon: <Users size={20} />,
    name: "Seats",
    info: "Number of team members with access.",
  },
  {
    icon: <Download size={20}></Download>,
    name: "Reports & Exports",
    info: "Download data format for analysis or sharing.",
  },
  {
    icon: <HandHelping size={20}></HandHelping>,
    name: "Support",
    info: "Available support channels for this plan.",
  },

  // {
  //   icon: <BellRing size={20}/>,
  //   name: "Notifications",
  //   info: "toolltip explanation",
  // },

  // {
  //   icon: <CodeXml size={20}/>,
  //   name: "API",
  //   info: "toolltip explanation",
  // },
];

type FEPlan = {
  name: string;
  id: string;
  custom: boolean;
  description: string;
  highlighted: boolean | string;
  monthlyPrice: string;
  monthlyPriceId: string;
  yearlyPriceId: string;
  yearlyPrice: string;
  features: string[];
};
const AI_PLATFORM_FEATURE_INDEX = 3;

const buildAiPlatformsText = (plan: Plan): string => {
  const overviewsEnabled = Boolean(plan.limits["ai.overviews.enabled"]);
  const aiModeEnabled = Boolean(
    plan.limits["ai.mode.enabled"] ?? plan.limits["ai.aimode.enabled"],
  );
  const parts = [
    plan.limits["ai.chatgpt.enabled"] ? "ChatGPT" : null,
    overviewsEnabled ? "Google AI Overview" : null,
    aiModeEnabled ? "AI mode" : null,
    plan.limits["ai.gemini.enabled"] ? "Gemini" : null,
    plan.limits["ai.perplexity.enabled"] ? "Perplexity" : null,
    plan.limits["ai.grok.enabled"] ? "Grok" : null,
    plan.limits["ai.copilot.enabled"] ? "Copilot" : null,
  ].filter(Boolean) as string[];
  return parts.join(", ");
};

const PlansPricing = () => {
  const [period, setPeriod] = useState("monthly");
  const [plans, setPlans] = useState<FEPlan[]>([]);
  const [loadingPrice, setLoadingPrice] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["getPlans"],
    queryFn: async () => {
      const result = await configAPI.getPlans();
      return result || [];
    },
    staleTime: Infinity,
  });
  const { data: meData, refetch } = useQuery({
    queryKey: ["getMeForPlans"],
    queryFn: async () => {
      return await authAPI.getMe();
    },
  });
  const user = meData?.user;
  const isWorkspaceMember =
    String(user?.tier || "")
      .trim()
      .toLowerCase() === "collaborator" ||
    String(user?.tier || "")
      .trim()
      .toLowerCase() === "company_member";
  const billingSubscriptionQueryKey = [
    "billing-subscription-for-plans",
    user?.id ?? "me",
  ];
  const { data: billingSubscription } = useQuery({
    queryKey: billingSubscriptionQueryKey,
    queryFn: billingAPI.getSubscription,
    enabled: !!user?.id && !isWorkspaceMember,
    retry: false,
  });
  const trackedPlanPageRef = useRef<string>("");
  const { data: meUsage } = useQuery({
    queryKey: ["getMeUsageForPlans"],
    queryFn: async () => {
      return await authAPI.getMeUsage();
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (data && Object.keys(data).length) {
      const preparedPlans = Object.values(data)
        .filter((pl) => pl.active)
        .sort((a: Plan, b: Plan) => Number(a.id) - Number(b.id))
        .map((plan) => {
          return {
            name: plan.name,
            id: plan.id,
            custom: plan.custom || false,
            description: plan.description,
            monthlyPrice: plan.custom ? plan.monthlyPriceId : plan.monthlyPrice,
            monthlyPriceId: plan.monthlyPriceId,
            yearlyPrice: plan.custom ? plan.yearlyPriceId : plan.yearlyPrice,
            yearlyPriceId: plan.yearlyPriceId,
            features: [
              `${plan.custom ? plan.customText?.prompts : `${plan.limits["prompts.max_tracked"]} prompts tracked`}`,
              `${plan.custom ? plan.customText?.brands : `${plan.limits["brands.max"]} ${plan.limits["brands.max"] > 1 ? "brands" : "brand"}`}`,
              `${plan.custom ? plan.customText?.countries : `${plan.limits["countries.max"]} ${plan.limits["countries.max"] > 1 ? "countries" : "country"}`}`,
              `${buildAiPlatformsText(plan)}`,
              `${plan.custom ? plan.customText?.runs : `${plan.limits["runs.monthly"]}`}`,
              // `${plan.limits["credits.monthly"]} credits / mo`,
              `Unlimited`,
              `${plan.custom ? plan.customText?.export : plan.limits["exports.csv.enabled"] && "CSV"}`,
              `${plan.custom ? plan.customText?.support : "Standard email & in-app chat"}`,
              // `Limited`,
              // `No`,
            ],
            highlighted: plan.metadata.label ? plan.metadata.label : false,
          };
        });
      setPlans(preparedPlans);
    }
  }, [data]);

  useEffect(() => {
    if (!user?.id || !meData) return;
    const usageCountriesCount = new Set(
      (meData.brands || [])
        .map((brand) =>
          String(brand?.country || "")
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean),
    ).size;
    const key = `${user.id}:${user.tier}:${meUsage?.runs?.used || 0}:${usageCountriesCount}`;
    if (trackedPlanPageRef.current === key) return;
    trackedPlanPageRef.current = key;

    capturePosthogEvent("plan_page_viewed", {
      current_plan: user?.tier || "trial",
      usage_prompt_runs: Number(meUsage?.runs?.used || 0),
      usage_countries: usageCountriesCount,
    });
  }, [meData, meUsage?.runs?.used, user?.id, user?.tier]);

  const shouldExpandAiPlatformsRow = plans.some((plan) => {
    const aiFeature = String(
      plan?.features?.[AI_PLATFORM_FEATURE_INDEX] || "",
    ).trim();
    return aiFeature.length > 40 || aiFeature.includes(",");
  });

  return (
    <div>
      <div className="flex py-4 mx-auto overflow-x-scroll">
        <Card
          className={`rounded-none rounded-tl-lg  rounded-bl-lg shadow-none flex flex-col justify-between bg-[#F5F5F5] min-w-[300px] flex-grow`}
        >
          <CardHeader>
            <CardTitle className="flex flex-col gap-2">
              <div className="mb-1 flex font-normal justify-between text-muted-foreground text-sm">
                <div>
                  <LocalizedText id="legacy.billed.16sw0ut" />
                </div>
              </div>
              <Tabs
                defaultValue="monthly"
                value={period}
                onValueChange={setPeriod}
                className="w-full"
              >
                <TabsList className="w-full bg-[#E5E5E5]">
                  <TabsTrigger value="monthly" className="cursor-pointer">
                    <LocalizedText id="legacy.monthly.hvlts0" />
                  </TabsTrigger>
                  <TabsTrigger value="yearly" className="cursor-pointer">
                    <LocalizedText id="legacy.yearly.1kkgywv" />{" "}
                    <Badge className="text-[#16A34A]" variant="outline">
                      <LocalizedText id="legacy.2.months.free.1f8xg68" />
                    </Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </CardTitle>
          </CardHeader>
          <CardContent className="w-full">
            <p className="text-muted-foreground text-sm">
              <LocalizedText id="legacy.key.features.6p1b0v" />
            </p>
            <ul className="text-sm ">
              {features.map((feature, index) => (
                <div
                  className={`flex flex-col ${
                    index === AI_PLATFORM_FEATURE_INDEX &&
                    shouldExpandAiPlatformsRow
                      ? "min-h-[84px] h-auto py-2"
                      : "h-[52px]"
                  }`}
                  key={index}
                >
                  <li
                    className={`flex gap-3 justify-between h-full ${
                      index === AI_PLATFORM_FEATURE_INDEX &&
                      shouldExpandAiPlatformsRow
                        ? "items-start"
                        : "items-center"
                    }`}
                  >
                    <div className="flex gap-2">
                      {feature.icon}
                      {feature.name}
                    </div>
                    <div>
                      <Tooltip>
                        <TooltipTrigger className="has-[>svg]:px-2" asChild>
                          <button>
                            <Info
                              size="17"
                              className="text-[#737373] cursor-pointer"
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {feature.info}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </li>
                  <Separator />
                </div>
              ))}
            </ul>
          </CardContent>
        </Card>
        {isLoading ? (
          <div className="w-full h-screen flex justify-center items-center">
            <Spinner variant="circle-filled" size={45}></Spinner>
          </div>
        ) : (
          plans.map((plan) => {
            const normalizedPlanName = String(plan.name || "")
              .trim()
              .toLowerCase();
            const normalizedSubscriptionPlan = String(
              billingSubscription?.plan || "",
            )
              .trim()
              .toLowerCase();
            const normalizedSubscriptionStatus = String(
              billingSubscription?.subscription_status || "",
            )
              .trim()
              .toLowerCase();
            const hasPaidSubscription = ["active", "trialing"].includes(
              normalizedSubscriptionStatus,
            );
            const isCurrentPlanBySubscription =
              hasPaidSubscription &&
              normalizedSubscriptionPlan === normalizedPlanName;
            const isCurrentPlanByTierFallback =
              !billingSubscription &&
              plan.id.toString() === String(user?.tier || "");
            const isStarterTrialing =
              normalizedPlanName === "starter" &&
              normalizedSubscriptionStatus === "trialing" &&
              isCurrentPlanBySubscription;
            const showCurrentPlanState =
              (isCurrentPlanBySubscription || isCurrentPlanByTierFallback) &&
              !isStarterTrialing;

            return (
              <Card
                key={plan.name}
                className={`rounded-none  shadow-none relative flex flex-col justify-between ${plan.highlighted ? "border-[#FF5E00] shadow-md rounded-md" : ""}`}
              >
                {plan.highlighted && (
                  <Badge
                    className="text-[#FF5E00] border-[#FF5E00] absolute -top-[12px] bg-white left-1/2 -translate-x-1/2"
                    variant="outline"
                  >
                    {plan.highlighted}
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-xl">
                    {plan.name}
                  </CardTitle>
                  <p className="text-muted-foreground text-sm h-[45px]">
                    {plan.description}
                  </p>
                  <p className="mb-3">
                    <span
                      className={`${period === "monthly" && "hidden"} line-through decoration-2 text-3xl text-muted-foreground font-light mr-1`}
                    >
                      {plan.custom
                        ? ""
                        : `$${parseInt(plan?.monthlyPrice) * 12}`}
                    </span>
                    <span className="text-3xl font-semibold">
                      {period === "monthly"
                        ? `${plan.custom ? "" : "$"}${plan.monthlyPrice}`
                        : `${plan.custom ? "" : "$"}${plan.yearlyPrice}`}
                    </span>
                    {!plan.custom && (
                      <span className="font-normal text-sm text-muted-foreground">
                        {period === "monthly" ? "/mo" : "/yearly"}
                      </span>
                    )}
                  </p>
                  <Button
                    disabled={showCurrentPlanState || loadingPrice}
                    onClick={async () => {
                      if (plan.custom) {
                        window.open("https://nobori.ai/demo", "_blank");
                        return;
                      }
                      setLoadingPrice(true);
                      const priceId =
                        period === "monthly"
                          ? plan.monthlyPriceId
                          : plan.yearlyPriceId;
                      try {
                        const data = await paymentAPI.subscribe(priceId);
                        if (data?.url) {
                          window.location.href = data.url;
                        } else {
                          setTimeout(async () => {
                            await Promise.all([
                              refetch(),
                              queryClient.invalidateQueries({
                                queryKey: billingSubscriptionQueryKey,
                              }),
                            ]);
                            setLoadingPrice(false);
                          }, 1000);
                        }
                      } catch {
                        setLoadingPrice(false);
                      }
                    }}
                    className={`w-full hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00] ${showCurrentPlanState ? "text-[#0A0A0A] hover:text-[#0A0A0A] bg-secondary hover:bg-secondary" : ""}`}
                  >
                    {showCurrentPlanState
                      ? "Current plan"
                      : plan.custom
                        ? "Book Demo"
                        : "Upgrade now"}
                  </Button>
                </CardHeader>

                <CardContent className="w-full">
                  <ul className="text-sm">
                    {plan.features.map((feature, index) => (
                      <div
                        className={`flex flex-col justify-center ${
                          index === AI_PLATFORM_FEATURE_INDEX &&
                          shouldExpandAiPlatformsRow
                            ? "min-h-[84px] h-auto py-2"
                            : "h-[52px]"
                        }`}
                        key={`${feature}${plan.name}`}
                      >
                        <li
                          className={`flex gap-3 h-full ${
                            index === AI_PLATFORM_FEATURE_INDEX &&
                            shouldExpandAiPlatformsRow
                              ? "items-start"
                              : "items-center"
                          }`}
                        >
                          <div>
                            {feature === "No" || feature === "Limited" ? (
                              <BadgePlus
                                size={20}
                                className="text-muted-foreground rotate-45"
                              />
                            ) : (
                              <BadgeCheck
                                size={20}
                                className="text-[#16A34A]"
                              />
                            )}
                          </div>
                          <span
                            className={
                              index === AI_PLATFORM_FEATURE_INDEX &&
                              shouldExpandAiPlatformsRow
                                ? "whitespace-normal break-words leading-5"
                                : ""
                            }
                          >
                            {feature}
                          </span>
                        </li>
                        <Separator className="" />
                      </div>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PlansPricing;
